import { Button } from '@ctw/shared/components/button';
import type {
  FilterChangeEvent,
  FilterItem,
  FilterOptionCheckbox,
  FilterOptionSelect,
} from '@ctw/shared/components/filter-bar/filter-bar-types';
import { displayFilterItem } from '@ctw/shared/components/filter-bar/filter-bar-utils';
import { Checkbox } from '@ctw/shared/components/form/checkbox';
import { Toggle } from '@ctw/shared/components/toggle';
import { partitionFilters } from '@ctw/shared/components/view-controls/promoted-filters';
import { type ModalLayout, useModal } from '@ctw/shared/context/modal-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { type ClassName, tw, twx } from '@ctw/shared/utils/tailwind';
import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { omit } from 'lodash-es';
import { useState } from 'react';

type FilterOptionValue =
  | FilterOptionCheckbox['values'][number]
  | FilterOptionSelect['values'][number];

const optionKey = (value: FilterOptionValue) => (typeof value === 'string' ? value : value.key);
const optionName = (value: FilterOptionValue) => (typeof value === 'string' ? value : value.name);

// The Filters modal opens fullbleed so its own sticky header can sit flush against the panel
// edge; FiltersModalContent adds the compensating body padding the fullbleed panel omits.
const FILTERS_MODAL_LAYOUT: ModalLayout = {
  mode: 'fit',
  overflowBehavior: 'scroll',
  fullbleed: true,
  showCloseButton: true,
};

// Resolve a filter's display to a plain string for use as an aria-label. Function-valued
// displays may return arbitrary ReactNodes, so fall back to the key unless the result is a string.
const filterLabel = (filter: FilterItem): string => {
  if (typeof filter.display === 'function') {
    const resolved = filter.display({});
    return typeof resolved === 'string' ? resolved : filter.key;
  }
  return filter.display;
};

export interface UseFiltersModalArgs {
  filters: Array<FilterItem>;
  selected: FilterChangeEvent;
  onUpdate: (filters: FilterChangeEvent) => void;
}

// Open the Filters modal the idiomatic way — via the shared ModalProvider — mirroring the other
// modal openers in the app (see use-vital-modal / use-bulk-action-modal). Returns a trigger the
// caller wires to its Filters button. Each open mounts a fresh FiltersModalContent, so staged
// edits reset to the committed `selected` every time the modal reopens.
export const useFiltersModal = ({ filters, selected, onUpdate }: UseFiltersModalArgs) => {
  const telemetry = useTelemetry();
  const { openModal, closeModal } = useModal({
    boundaryName: 'FiltersModal',
    layout: FILTERS_MODAL_LAYOUT,
  });

  return () => {
    openModal({
      telemetry,
      action: {
        label: 'Filters',
        title: 'Filters',
        content: {
          body: (
            <FiltersModalContent
              filters={filters}
              selected={selected}
              onUpdate={onUpdate}
              onClose={closeModal}
            />
          ),
        },
      },
    });
  };
};

export interface FiltersModalContentProps {
  id?: string;
  testId?: string;
  className?: ClassName;
  filters: Array<FilterItem>;
  selected: FilterChangeEvent;
  onUpdate: (filters: FilterChangeEvent) => void;
  onClose: () => void;
}

export const FiltersModalContent = ({
  id,
  testId,
  className,
  filters,
  selected,
  onUpdate,
  onClose,
}: FiltersModalContentProps) => {
  // Staged selection is seeded from the committed `selected` on mount. The modal opener remounts
  // this component on every open, so reopening naturally discards any uncommitted staged edits.
  const [staged, setStaged] = useState<FilterChangeEvent>(selected);

  const { optionGroups, toggles } = partitionFilters(filters);

  const toggleCheckboxValue = (filterKey: string, value: string) => {
    setStaged((previousStaged) => {
      const currentFilter = previousStaged[filterKey];
      const currentValues =
        currentFilter?.type === 'checkbox' && Array.isArray(currentFilter.selected)
          ? currentFilter.selected
          : [];
      const nextValues = currentValues.includes(value)
        ? currentValues.filter((selectedValue) => selectedValue !== value)
        : [...currentValues, value];

      if (nextValues.length === 0) {
        return omit(previousStaged, filterKey);
      }

      return {
        ...previousStaged,
        [filterKey]: { key: filterKey, type: 'checkbox', selected: nextValues },
      };
    });
  };

  const setToggleValue = (filterKey: string, checked: boolean) => {
    setStaged((previousStaged) =>
      checked
        ? { ...previousStaged, [filterKey]: { key: filterKey, type: 'tag', selected: true } }
        : omit(previousStaged, filterKey),
    );
  };

  const setSelectValue = (filterKey: string, value: string) => {
    setStaged((previousStaged) => ({
      ...previousStaged,
      [filterKey]: { key: filterKey, type: 'select', selected: value },
    }));
  };

  const isChecked = (filterKey: string, value: string) => {
    const currentFilter = staged[filterKey];
    return (
      currentFilter?.type === 'checkbox' &&
      Array.isArray(currentFilter.selected) &&
      currentFilter.selected.includes(value)
    );
  };

  const isSelected = (filterKey: string, value: string) => {
    const currentFilter = staged[filterKey];
    return currentFilter?.type === 'select' && currentFilter.selected === value;
  };

  const isToggled = (filterKey: string) => {
    const currentFilter = staged[filterKey];
    return currentFilter?.type === 'tag' && currentFilter.selected === true;
  };

  return (
    <div
      id={id}
      data-testid={testId}
      // No bottom padding here (px/pt only) — see the footer's own pb-4 below for why.
      className={twx(className, 'flex flex-col gap-6 px-3 pt-3 sm:min-h-[24rem] sm:min-w-[36rem]')}
    >
      <header
        className={tw`sticky top-0 z-10 -mt-10 mb-8 flex items-center border-divider-main border-b border-solid bg-white pt-5 pb-4`}
      >
        <h2 className={tw`font-semibold text-content-main text-xl`}>Filters</h2>
      </header>
      {optionGroups.map((filter) => {
        const values =
          filter.type === 'select' ? filter.values : (filter as FilterOptionCheckbox).values;
        return (
          <fieldset key={filter.key} className={tw`flex flex-col gap-3 border-0 p-0`}>
            <legend className={tw`mb-2 font-semibold text-base capitalize`}>
              {displayFilterItem(filter, {})}
            </legend>
            {values.length === 0 ? (
              <p className={tw`text-content-subtle text-sm`}>No options available</p>
            ) : (
              <div className={tw`grid grid-cols-2 gap-x-6 gap-y-3`}>
                {filter.type === 'select'
                  ? filter.values.map((value) => {
                      const key = optionKey(value);
                      return (
                        <label
                          key={key}
                          className={tw`flex cursor-pointer items-center gap-2 text-sm`}
                        >
                          <input
                            type="radio"
                            name={filter.key}
                            checked={isSelected(filter.key, key)}
                            onChange={() => setSelectValue(filter.key, key)}
                            className={tw`h-4 w-4 cursor-pointer accent-primary-main`}
                          />
                          <span className={tw`capitalize`}>{optionName(value)}</span>
                        </label>
                      );
                    })
                  : (filter as FilterOptionCheckbox).values.map((value) => {
                      const key = optionKey(value);
                      return (
                        <Checkbox
                          key={key}
                          className={tw`capitalize`}
                          name={`${filter.key}-${key}`}
                          label={optionName(value)}
                          checked={isChecked(filter.key, key)}
                          onChange={() => toggleCheckboxValue(filter.key, key)}
                        />
                      );
                    })}
              </div>
            )}
          </fieldset>
        );
      })}

      {optionGroups.length === 0 && toggles.length === 0 && (
        <p
          className={tw`flex flex-1 items-center justify-center text-center text-content-subtle text-sm`}
        >
          No filters are available for this view.
        </p>
      )}

      {toggles.length > 0 && (
        <section className={tw`flex flex-col gap-3`}>
          <h3 className={tw`font-semibold text-base`}>Additional Options</h3>
          {toggles.map((filter) => (
            <div key={filter.key} className={tw`flex items-center justify-between`}>
              <span className={tw`text-sm capitalize`}>{displayFilterItem(filter, {})}</span>
              <Toggle
                checked={isToggled(filter.key)}
                onChange={(checked) => setToggleValue(filter.key, checked)}
                label={filterLabel(filter)}
              />
            </div>
          ))}
        </section>
      )}

      <div
        // pb-4 lives here, not as padding on the scroll container: this footer's own bg-white
        // box is what needs to cover the gap below the buttons. Padding on the scroll container
        // instead would leave a transparent strip below the sticky footer's own (smaller) box —
        // scrolled-past content shows through it, since nothing there has an opaque background.
        className={tw`sticky bottom-0 z-10 flex items-center justify-between border-divider-main border-t border-solid bg-white pt-4 pb-4`}
      >
        <Button type="button" variant="secondary" onClick={() => setStaged({})}>
          <FontAwesomeIcon icon={faTrash} className={tw`mr-2 w-4`} />
          Clear All Filters
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={() => {
            onUpdate(staged);
            onClose();
          }}
        >
          Update
        </Button>
      </div>
    </div>
  );
};
